<template>
    <div class="status-container shadow-sm">
        <div class="toolbar">
            <div class="form-check">
                <input type="checkbox" class="form-check-input" id="selectAllCheckbox" v-model="allSelected" />
                <label class="form-check-label" for="selectAllCheckbox">{{ $t("admin.status.select_all") }}</label>
            </div>

            <button class="btn btn-outline-secondary btn-sm" v-bind:disabled="selectedMapObjects.length == 0" v-on:click="handleSelectedMapObjectsStatusButtonClicked(null)">{{ $t("admin.status.no_status") }}</button>
            <button v-for="mapObjectStatusTemplate of mapObjectStatusTemplates" v-bind:key="'toolbar_' + mapObjectStatusTemplate.id" class="btn btn-outline-primary btn-sm" v-bind:style="statusButtonStyle(mapObjectStatusTemplate)"
                    v-on:click="handleSelectedMapObjectsStatusButtonClicked(mapObjectStatusTemplate)" v-bind:disabled="selectedMapObjects.length == 0">
                {{ mapObjectStatusTemplateTitle(mapObjectStatusTemplate) }}
            </button>
        </div>

        <div class="list-container">
            <div class="map-object-list">
                <div class="spacer header"></div>
                <div class="form-check map-object-checkbox header"></div>
                <div class="map-object-name header">{{ $t("admin.status.name_column") }}</div>
                <div class="map-object-toolbar header">{{ $t("admin.status.status_column") }}</div>
                <div class="map-object-status-string header">{{ $t("admin.status.status_string") }}</div>
                <div class="map-object-update-on header">{{ $t("admin.status.last_updated_on_column") }}</div>

                <template v-for="mapObject of mapObjects">
                    <template v-if="mapObject.type == 'layer'">
                        <div class="spacer" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_spacer'"></div>
                        <div class="form-check map-object-checkbox" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_checkbox'"></div>
                        <div class="map-object-name map-object-layer-name" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_name'">
                            <div>{{ mapObjectName(mapObject) }}</div>
                        </div>
                        <div class="map-object-toolbar" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_toolbar'"></div>
                        <div class="map-object-status-string" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_status_string'"></div>
                        <div class="map-object-update-on" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_updated_on'"></div>
                    </template>
                    <template v-if="mapObject.type != 'layer'">
                        <div class="spacer" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_spacer'"></div>
                        <div class="form-check map-object-checkbox" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_checkbox'">
                            <input type="checkbox" class="form-check-input" v-bind:checked="mapObjectSelected(mapObject)" v-on:change="handleMapObjectSelected(mapObject, $event)" />
                        </div>
                        <div class="map-object-name" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_name'">
                            <div>{{ mapObjectName(mapObject) }}</div>
                        </div>
                        <div class="map-object-toolbar" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_toolbar'">
                            <button v-bind:class="noStatusButtonClass(mapObject)" v-on:click="handleMapObjectStatusButtonClicked(mapObject, null)">{{ $t("admin.status.no_status") }}</button>
                            <button v-for="mapObjectStatusTemplate of mapObjectStatusTemplates" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectStatusTemplate.id" v-bind:class="mapObjectStatusButtonClass(mapObject, mapObjectStatusTemplate)" 
                                    v-bind:style="mapObjectStatusButtonStyle(mapObject, mapObjectStatusTemplate)" v-on:click="handleMapObjectStatusButtonClicked(mapObject, mapObjectStatusTemplate)">
                                {{ mapObjectStatusTemplateTitle(mapObjectStatusTemplate) }}
                            </button>
                        </div>
                        <div class="map-object-status-string" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_status_string'">
                            <textarea v-if="mapSpec && mapSpec.languageAvailability.english" class="form-control" rows="1" 
                                v-bind:placeholder="mapSpec.languageAvailability.french ? $t('enums.languages.english') : ''" 
                                v-bind:value="mapObjectStatusStatusString(mapObject, 'en')" 
                                v-on:change="handleMapObjectStatusStatusStringChanged(mapObject, 'en', $event.target.value)"></textarea>
                            <textarea v-if="mapSpec && mapSpec.languageAvailability.french" class="form-control" rows="1" 
                                v-bind:placeholder="mapSpec.languageAvailability.english ? $t('enums.languages.french') : ''" 
                                v-bind:value="mapObjectStatusStatusString(mapObject, 'fr')"
                                v-on:change="handleMapObjectStatusStatusStringChanged(mapObject, 'fr', $event.target.value)"></textarea>
                        </div>
                        <div class="map-object-update-on" v-bind:style="listCellBackgroundColor(mapObject)" v-bind:key="mapObjectId(mapObject) + '_' + mapObjectId(mapObject) + '_updated_on'">
                            {{ mapObjectStatusLastUpdate(mapObject) }}
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>    
</template>

<script>
import FromOMBMapSpec from '@/map/spec/FromOMBMapSpec'
import MapService from "@/service/MapService"
import MapObjectStatusService from "@/service/MapObjectStatusService"
import MapObjectStatus from "@/model/MapObjectStatus"
import LangUtils from "@/utils/LangUtils"
import * as moment from 'moment';
import * as tinycolor from 'tinycolor2';

export default {
    data() {
        return {
            mapService: new MapService(),
            mapObjectStatusService: new MapObjectStatusService(this.$i18n.locale),
            mapObjectStatusTemplates: null,
            mapSpec: null,
            mapObjects: null,
            allMapObjectStatus: [],
            selectedMapObjects: []
        }
    },

    props: {
        publisherId: String,
        mapId: String
    },

    computed: {
        allSelected: {
            get: function() {
                if (this.selectedMapObjects != null && this.mapObjects != null) {
                    return this.selectedMapObjects.length == this.mapObjects.length;
                } else {
                    return false;
                }
            },

            set: function(newValue) {
                if (newValue == true) {
                    if (this.mapObjects != null) {
                        for (var mapObject of this.mapObjects) {
                            this.selectedMapObjects.push(mapObject);
                        }
                    }
                } else {
                    this.selectedMapObjects = [];
                }
            }
        }
    },

    methods: {
        loadMapObjects() {
            if (this.publisherId && this.mapId) {
                const layerMapObjects = this.mapSpec.mapObjectStructure;

                const flattenMapObjects = [];
                for (var layerMapObject of layerMapObjects) {
                    flattenMapObjects.push(layerMapObject);

                    for (const child of layerMapObject.children) {
                        flattenMapObjects.push(child);
                    }
                }
                
                this.mapObjects = flattenMapObjects;
            } else {
                this.mapObjects = null;
            }
        },

        loadMapObjectStatusTemplates() {
            this.mapObjectStatusService.loadMapObjectStatusTemplates(this.publisherId, true).then((mapObjectStatusTemplates) => {
                this.mapObjectStatusTemplates = mapObjectStatusTemplates;
            }).catch((error) => {
                console.log(error);
            });
        },

        loadMapObjectStatus() {
            return new Promise((resolve, reject) => {
                if (this.publisherId && this.mapId) {
                    this.mapService.loadMapObjectStatusForMap(this.publisherId, this.mapId).then((allMapObjectStatus) => {
                        this.allMapObjectStatus = allMapObjectStatus;
                        resolve();
                    }).catch((error) => {
                        console.log(error);
                        reject(error);
                    });
                } else {
                    resolve();
                }
            });
        },

        saveMapObjectStatus(mapObjects, newStatusTemplate, statusStringLanguage = null, statusString = null) {
            if (newStatusTemplate || statusStringLanguage || statusString) {
                const statusToSave = [];
                for (var mapObject of mapObjects) {
                    const mapObjectStatus = this.findOrCreateMapObjectStatusForMapObject(this.mapObjectId(mapObject));
                    if (newStatusTemplate) {
                        mapObjectStatus.mapObjectStatusTemplateId = newStatusTemplate.id;
                    }

                    if (statusStringLanguage || statusString) {
                        if (!mapObjectStatus.statusString) {
                            mapObjectStatus.statusString = { };
                        }

                        mapObjectStatus.statusString[statusStringLanguage] = statusString;
                    }

                    mapObjectStatus.lastUpdate = new Date().getTime();
                    statusToSave.push(mapObjectStatus);
                    this.allMapObjectStatus.push(mapObjectStatus);
                }

                this.mapService.saveMapObjectStatusArray(this.publisherId, statusToSave).then(() => {
                    if (statusToSave.length > 1) {
                        this.loadMapObjectStatus();
                    }
                }).catch((error) => {
                    console.log(error);
                });
            } else {
                const statusIdsToDelete = [];

                for (mapObject of mapObjects) {
                    const mapObjectStatus = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
                    if (mapObjectStatus) {
                        statusIdsToDelete.push(mapObjectStatus.id);
                    }
                }
                this.mapService.deleteMapObjectStatus(this.publisherId, statusIdsToDelete).then(() => {
                    this.loadMapObjectStatus();
                }).catch((error) => {
                    console.log(error);
                });
            }
        },

        listCellBackgroundColor(mapObject) {
            if (mapObject.type == "layer") {
                return "background-color: #555555";
            } else {
                const index = this.mapObjects.indexOf(mapObject);
                if (!(index % 2)) {
                    return "background-color: #f5f5f5;"
                } else {
                    return "";
                }
            }
        },

        statusButtonStyle(mapObjectStatusTemplate) {
            var color = mapObjectStatusTemplate.color;
            if (color) {
                var fontColor = color;
                var borderColor = color;
                if (tinycolor(color).getBrightness() > 205) {
                    borderColor = tinycolor(color).darken(10);
                    fontColor = tinycolor(color).darken(50);
                }

                return "color: " + fontColor + "; border-color: " + borderColor;
            } else {
                return "";
            }
        },

        mapObjectId(mapObject) {
            if (mapObject) {
                if (mapObject.uid) {
                    return mapObject.uid;
                } else if (mapObject.id) {
                    return mapObject.id;
                } else if (mapObject.uniqueIdentifier) {
                    return mapObject.uniqueIdentifier;
                } else {
                    return LangUtils.valueForLanguage(mapObject.name, this.$i18n.locale);
                }
            } else {
                return null;
            }
        },

        mapObjectStatusButtonClass(mapObject, mapObjectStatusTemplate) {
            const color = mapObjectStatusTemplate.color;
            if (color) {
                const mapObjectStatus = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
                if (mapObjectStatus && mapObjectStatus.mapObjectStatusTemplateId == mapObjectStatusTemplate.id) {
                    return "btn btn-primary btn-sm";
                } else {
                    return "btn btn-outline-primary btn-sm";
                }
            } else {
                return "";
            }
        },

        mapObjectStatusButtonStyle(mapObject, mapObjectStatusTemplate) {
            if (mapObjectStatusTemplate) {
                const color = mapObjectStatusTemplate.color;
                if (color) {
                    const mapObjectStatus = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
                    if (mapObjectStatus && mapObjectStatus.mapObjectStatusTemplateId == mapObjectStatusTemplate.id) {
                        var fontColor = "#ffffff";
                        var borderColor = color;
                        var backgroundColor = color;

                        if (tinycolor(color).getBrightness() > 205) {
                            fontColor = tinycolor(color).darken(100);
                            borderColor = tinycolor(color).darken(5);
                        }

                        return "color: " + fontColor + "; border-color: " + borderColor + "; background-color: " + backgroundColor + ";";
                    } else {
                        fontColor = color;
                        borderColor = color;

                        if (tinycolor(color).getBrightness() > 205) {
                            fontColor = tinycolor(color).darken(25);
                            borderColor = tinycolor(color).darken(10);
                        }

                        return "color: " + fontColor + "; border-color: " + borderColor;
                    }
                } else {
                    return "";
                }
            } else {
                const mapObjectStatus = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
                console.log(mapObjectStatus);
                if (!mapObjectStatus) {
                    return "background-color: #eeeeee";
                } else {
                    return "";
                }
            }
        },

        noStatusButtonClass(mapObject) {
            const mapObjectStatus = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
            if (!mapObjectStatus) {
                return "btn btn-secondary btn-sm";
            } else {
                return "btn btn-outline-secondary btn-sm";
            }
        },

        mapObjectName(mapObject) {
            return LangUtils.valueForLanguage(mapObject.name, this.$i18n.locale);
        },

        mapObjectStatusStatusString(mapObject, language) {
            const status = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
            if (status && status.statusString) {
                return status.statusString[language];
            } else {
                return null;
            }
        },

        mapObjectStatusLastUpdate(mapObject) {
            const status = this.findMapObjectStatusForMapObject(this.mapObjectId(mapObject));
            if (status) {
                if (status.lastUpdate) {
                    return moment(new Date(status.lastUpdate)).format("YYYY-MM-DD hh:mm");
                } else {
                    return this.$t("admin.status.not_available_message");
                }
            } else {
                return this.$t("admin.status.no_status_message");
            }
        },

        mapObjectSelected(mapObject) {
            return this.selectedMapObjects.indexOf(mapObject) > -1;
        },

        mapObjectStatusTemplateTitle(mapObjectStatusTemplate) {
            return LangUtils.valueForLanguage(mapObjectStatusTemplate.title, this.$i18n.locale);
        },

        findMapObjectStatusForMapObject(mapObjectId) {
            for (var mapObjectStatus of this.allMapObjectStatus) {
                if (mapObjectStatus.mapObjectId == mapObjectId) {
                    return mapObjectStatus;
                }
            }
            return null;
        },

        findOrCreateMapObjectStatusForMapObject(mapObjectId) {
            var status = this.findMapObjectStatusForMapObject(mapObjectId);
            if (status == null) {
                status = new MapObjectStatus();
                status.mapIds = {};
                status.mapIds[this.mapId] = true;
                status.mapObjectId = mapObjectId;
            }
            return status;
        },

        handleMapObjectSelected(mapObject, event) {
            const checked = event.target.checked;
            if (checked) {
                this.selectedMapObjects.push(mapObject);
            } else {
                const index = this.selectedMapObjects.indexOf(mapObject);
                if (index > -1) {
                    this.selectedMapObjects.splice(index, 1);
                }
            }
        },

        handleMapObjectStatusButtonClicked(mapObject, mapObjectStatusTemplate) {
            this.saveMapObjectStatus([ mapObject ], mapObjectStatusTemplate);
        },

        handleSelectedMapObjectsStatusButtonClicked(mapObjectStatusTemplate) {
            this.saveMapObjectStatus(this.selectedMapObjects, mapObjectStatusTemplate);
        },

        handleMapObjectStatusStatusStringChanged(mapObject, language, newValue) {
            this.saveMapObjectStatus([ mapObject ], null, language, newValue)
        }
    },

    mounted() {
        if (this.publisherId && this.mapId) {
            this.mapService.loadMapForPublisher(this.publisherId, this.mapId).then((map) => {
                var selectedBundleId = null;
                if (map.bundleIds) {
                    for (var bundleId in map.bundleIds) {
                        if (map.bundleIds[bundleId]) {
                            selectedBundleId = bundleId;
                            break;
                        }
                    }
                }

                this.mapSpec = new FromOMBMapSpec(this.$store.state.currentUser, this.publisherId, selectedBundleId, this.mapId, this.$i18n.locale, this.$i18n);
                this.mapSpec.load().then(() => {
                    this.loadMapObjectStatusTemplates();
                    this.loadMapObjectStatus();
                    this.loadMapObjects();
                }).catch((error) => {
                    console.log(error);
                });
            }).catch((error) => {
                console.log(error);
            });
        }
    }
}
</script>

<style scoped>
.status-container {
    background-color: #ffffff;
    margin: 0 auto;
    padding: 24px;
    text-align: left;
    width: 100%;
    height: 100%;
}

.top-toolbar {
    padding-left: 16px;
}

.toolbar {
    display: table-cell;
    height: 38px;
    padding: 0px 16px;
    padding-top: 16px;
    vertical-align: middle;
}

.toolbar .form-check {
    display: inline-block;
    margin-right: 24px;
}

.toolbar button {
    margin-right: 8px;
}

.list-container {
    overflow: scroll;
    width: 100%;
    height: 100%;
}

.map-object-list {
    display: grid;
    grid-template-columns: 15px 30px auto auto 30% auto;
    list-style-type: none;
    align-items: stretch;
    margin-top: 24px;
    padding: 0px;
    padding-bottom: 24px;
    overflow: scroll;
}

.map-object-checkbox {
    margin-bottom: 0px;
    padding-top: 8px;
    padding-bottom: 8px;
}

.map-object-name {
    font-weight: bold;
    padding: 8px 0px;
}

.map-object-toolbar {
    padding: 8px 0px;
}

.map-object-toolbar button {
    margin-right: 8px;
}

.map-object-status-string {
    display: grid;
    row-gap: 8px;
    padding: 8px 0px;
    padding-right: 8px;
}

.map-object-update-on {
    padding: 8px 0px;
}

.header {
    font-weight: bold;
}

.map-object-layer-name {
    color: #ffffff;
}
</style>